import React, { useState, useEffect } from "react";
import { User, Phone, Mail, Shield, CheckCircle2, Upload, AlertCircle, FileText, Camera } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, prefixUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

export default function UpdateProfile() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [selfieUrl, setSelfieUrl] = useState("");
  const [idProofUrl, setIdProofUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadingSelfie, setUploadingSelfie] = useState(false);
  const [uploadingIdProof, setUploadingIdProof] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [submittingVerification, setSubmittingVerification] = useState(false);
  const [bio, setBio] = useState("");
  const [experienceYears, setExperienceYears] = useState("");
  const [specialisations, setSpecialisations] = useState("");
  const [localities, setLocalities] = useState("");

  const [emailOtpSending, setEmailOtpSending] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpCode, setEmailOtpCode] = useState("");
  const [verifyingEmailOtp, setVerifyingEmailOtp] = useState(false);



  const handleSendEmailOtp = async () => {
    if (!email.trim()) {
      toast.error("Please enter a valid email address first");
      return;
    }
    setEmailOtpSending(true);
    try {
      const { data } = await api.post("/auth/otp/send", { email: email.trim() });
      setEmailOtpSent(true);
      if (data?.otp) {
        setEmailOtpCode(data.otp);
      }
      toast.success(`Verification OTP code sent to ${email}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmailOtpSending(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!emailOtpCode.trim()) {
      toast.error("Please enter the 6-digit OTP code");
      return;
    }
    setVerifyingEmailOtp(true);
    try {
      await api.post("/auth/otp/verify", {
        email: email.trim(),
        otp: emailOtpCode.trim(),
        user_id: user.id,
      });
      toast.success("Email address verified successfully!");
      setEmailOtpSent(false);
      setEmailOtpCode("");
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setVerifyingEmailOtp(false);
    }
  };

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setPhone(user.phone || "");
      setEmail(user.email || "");
      setSelfieUrl(user.selfie_url || "");
      setIdProofUrl(user.id_proof_url || "");
      
      const raw = user.raw || {};
      setBio(raw.bio || "");
      setExperienceYears(raw.experience_years || "");
      setSpecialisations(raw.specialisations || "");
      setLocalities(raw.localities || "");
    }
  }, [user]);

  const handleUpdateInfo = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/users/me", { name, phone, email });
      toast.success("Profile details updated successfully");
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateProfessionalInfo = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/users/me", {
        bio,
        experience_years: experienceYears,
        specialisations,
        localities
      });
      toast.success("Professional details updated successfully");
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAvatarUpload = async (file) => {
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("asset_type", "avatar");
      
      const { data } = await api.post("/upload", fd);
      
      const fileUrl = prefixUrl(data?.url || data?.file_url);
      if (!fileUrl) throw new Error("No URL returned from upload");

      await api.put("/users/me", { avatar_url: fileUrl });
      toast.success("Profile picture updated successfully");
      await refreshUser();
    } catch (err) {
      toast.error(`Upload failed: ${formatApiError(err)}`);
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Identity upload is intentionally two-step: upload bytes into protected
  // storage first, then attach only the returned backend URL to this user.
  const handleMediaUpload = async (file, type) => {
    if (!file) return;
    const isSelfie = type === "selfie";
    if (isSelfie) setUploadingSelfie(true);
    else setUploadingIdProof(true);

    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("asset_type", type);
      
      const { data } = await api.post("/upload", fd);
      
      const fileUrl = prefixUrl(data?.url || data?.file_url);
      if (!fileUrl) throw new Error("No URL returned from upload");

      if (isSelfie) {
        await api.post("/users/me/selfie", { url: fileUrl });
        setSelfieUrl(fileUrl);
        toast.success("Selfie uploaded successfully");
      } else {
        await api.post("/users/me/id-proof", { url: fileUrl });
        setIdProofUrl(fileUrl);
        toast.success("ID proof uploaded successfully");
      }
      await refreshUser();
    } catch (err) {
      toast.error(`Upload failed: ${formatApiError(err)}`);
    } finally {
      if (isSelfie) setUploadingSelfie(false);
      else setUploadingIdProof(false);
    }
  };

  // Submission does not send verification flags or URLs. The backend reloads
  // both from the authenticated account before creating the admin review item.
  const handleSubmitVerification = async () => {
    setSubmittingVerification(true);
    try {
      await api.post("/owner-verification/submit", {});
      toast.success("Identity documents submitted for admin review");
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmittingVerification(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="update-profile-page">
      <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-border/30">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">My Profile</p>
          <h1 className="font-display text-4xl font-black tracking-tight text-primary">Update Profile Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your account information, phone number, and verification documents.</p>
        </div>
        <div className="flex items-center gap-3 shrink-0 bg-card p-3 rounded-2xl border border-border shadow-sm">
          <div className="w-16 h-16 rounded-full overflow-hidden border border-primary/20 bg-muted flex items-center justify-center shadow-inner relative group cursor-pointer">
            {user.avatar_url ? (
              <img src={prefixUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-bold text-primary bg-primary/10 w-full h-full flex items-center justify-center">{String(user.name || "U").charAt(0).toUpperCase()}</span>
            )}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center z-10">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif"
              className="absolute inset-0 opacity-0 cursor-pointer z-20"
              onChange={(e) => handleAvatarUpload(e.target.files?.[0])}
              disabled={uploadingAvatar}
            />
            {uploadingAvatar && (
              <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-30">
                <Upload className="w-5 h-5 animate-bounce text-primary" />
              </div>
            )}
          </div>
          <div>
            <h4 className="font-bold text-foreground">{user.name || "User"}</h4>
            <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{user.role}</p>
          </div>
        </div>
      </header>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Left Side: Verification Status Card */}
        <div className="md:col-span-1 space-y-6">
          <Card className="overflow-hidden border border-border/80 shadow-md">
            <div className="h-2 bg-gradient-to-r from-primary via-indigo-500 to-emerald-400" />
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" /> Verification Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col items-center py-4 bg-muted/20 rounded-2xl border border-dashed border-border/60">
                {user.verified_owner ? (
                  <>
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-2" />
                    <Badge className="bg-emerald-500 hover:bg-emerald-600 font-bold px-3 py-1">Verified Member</Badge>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-12 h-12 text-amber-500 mb-2" />
                    <Badge variant="secondary" className="font-bold px-3 py-1">Verification Pending</Badge>
                  </>
                )}
                <p className="text-[11px] text-muted-foreground mt-2 text-center px-4">
                  {user.verified_owner 
                    ? "Your account is verified. You can list properties with the Verified Owner/Agent badge."
                    : "Upload your selfie and government-issued ID proof to activate premium listing capabilities."}
                </p>
              </div>

              <div className="space-y-3 pt-2 text-xs">
                <div className="flex justify-between items-center py-2 border-b border-border/60">
                  <span className="text-muted-foreground font-medium">Selfie Photo</span>
                  <Badge variant={user.selfie_status === "verified" ? "default" : "outline"} className={`capitalize font-bold text-[10px] ${user.selfie_status === "verified" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : ""}`}>
                    {user.selfie_status || "Not Uploaded"}
                  </Badge>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-muted-foreground font-medium">ID Document</span>
                  <Badge variant={user.id_proof_status === "verified" ? "default" : "outline"} className={`capitalize font-bold text-[10px] ${user.id_proof_status === "verified" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : ""}`}>
                    {user.id_proof_status || "Not Uploaded"}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Side: Account details and Uploads */}
        <div className="md:col-span-2 space-y-8">
          {/* Form Card */}
          <Card className="border border-border/80 shadow-md">
            <CardHeader>
              <CardTitle className="text-xl font-bold">Personal Details</CardTitle>
              <CardDescription>Update your display name and contact phone number.</CardDescription>
            </CardHeader>
            <form onSubmit={handleUpdateInfo}>
              <CardContent className="space-y-4">
                <div className="grid gap-1.5">
                  <Label htmlFor="prof-name">Full Name</Label>
                  <div className="relative">
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input id="prof-name" value={name} onChange={(e) => setName(e.target.value)} className="pl-9 bg-background" required />
                  </div>
                </div>

                <div className="grid gap-1.5">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="prof-email">Email Address</Label>
                    {user.email_verified ? (
                      <Badge className="bg-emerald-500 hover:bg-emerald-600 font-bold px-2 py-0.5 text-xs text-white">
                        <CheckCircle2 className="w-3 h-3 mr-1 inline" /> Verified
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-amber-500/10 text-amber-700 dark:text-amber-300 font-bold px-2 py-0.5 text-xs">
                        <AlertCircle className="w-3 h-3 mr-1 inline" /> Unverified
                      </Badge>
                    )}
                  </div>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input 
                      id="prof-email" 
                      value={email} 
                      onChange={(e) => setEmail(e.target.value)} 
                      className={`pl-9 bg-background ${user.email ? "bg-muted text-muted-foreground cursor-not-allowed" : ""}`} 
                      disabled={!!user.email} 
                    />
                  </div>
                  {!user.email_verified && user.email && (
                    <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl space-y-2">
                      <div className="flex justify-between items-center text-xs">
                        <span className="font-bold text-amber-800 dark:text-amber-300">Email verification required</span>
                        <Button type="button" size="sm" variant="outline" onClick={handleSendEmailOtp} disabled={emailOtpSending} className="h-7 text-xs font-bold border-amber-600 text-amber-700 hover:bg-amber-600/10">
                          {emailOtpSending ? "Sending OTP..." : "Send Verification OTP"}
                        </Button>
                      </div>
                      {emailOtpSent && (
                        <div className="flex gap-2 pt-1">
                          <Input value={emailOtpCode} onChange={(e) => setEmailOtpCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} placeholder="6-digit Email OTP" className="h-8 font-mono text-xs bg-background" maxLength={6} />
                          <Button type="button" size="sm" onClick={handleVerifyEmailOtp} disabled={verifyingEmailOtp} className="h-8 text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white shrink-0">
                            {verifyingEmailOtp ? "Verifying..." : "Verify Email"}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  {user.email ? (
                    <span className="text-[10px] text-muted-foreground">Contact support to change your account email.</span>
                  ) : (
                    <span className="text-[10px] text-amber-500 font-semibold">Please add your email address to complete your profile.</span>
                  )}
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="prof-phone">Phone Number</Label>
                  <div className="relative">
                    <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input id="prof-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="pl-9 bg-background" placeholder="+919999999999" />
                  </div>
                </div>
              </CardContent>
              <CardFooter className="justify-end border-t border-border/40 pt-4">
                <Button type="submit" disabled={busy} className="font-bold">{busy ? "Saving Details..." : "Save Details"}</Button>
              </CardFooter>
            </form>
          </Card>

          {/* Documents Card */}
          <Card className="border border-border/80 shadow-md">
            <CardHeader>
              <CardTitle className="text-xl font-bold">Verification Documents</CardTitle>
              <CardDescription>Upload supporting files for platform verification check.</CardDescription>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-6">
              {/* Selfie Upload Section */}
              <div className="space-y-3">
                <Label className="font-bold">Selfie Verification</Label>
                <div className="border border-border/80 rounded-2xl p-4 flex flex-col items-center justify-center text-center h-48 bg-muted/10 relative overflow-hidden group">
                  {selfieUrl ? (
                    <>
                      <img src={prefixUrl(selfieUrl)} alt="Selfie" className="w-full h-full object-cover absolute inset-0 z-0" />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center z-10 p-4">
                        <Camera className="w-8 h-8 text-white mb-2" />
                        <p className="text-xs text-white font-semibold">Change selfie photo</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Camera className="w-8 h-8 text-muted-foreground mb-2" />
                      <p className="text-xs text-muted-foreground font-medium mb-1">Selfie with ID document</p>
                      <p className="text-[10px] text-muted-foreground/60 px-4">JPEG/PNG formats under 5MB</p>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif"
                    id="upload-selfie"
                    onChange={(e) => handleMediaUpload(e.target.files?.[0], "selfie")}
                    className="absolute inset-0 opacity-0 cursor-pointer z-20"
                    disabled={uploadingSelfie}
                  />
                  {uploadingSelfie && (
                    <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-30">
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="w-6 h-6 animate-bounce text-primary" />
                        <span className="text-xs font-semibold">Uploading...</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ID Proof Upload Section */}
              <div className="space-y-3">
                <Label className="font-bold">Government ID Proof</Label>
                <div className="border border-border/80 rounded-2xl p-4 flex flex-col items-center justify-center text-center h-48 bg-muted/10 relative overflow-hidden group">
                  {idProofUrl ? (
                    <>
                      {idProofUrl.endsWith(".pdf") ? (
                        <div className="flex flex-col items-center justify-center h-full">
                          <FileText className="w-12 h-12 text-primary mb-2" />
                          <span className="text-xs font-medium truncate max-w-[150px]">ID_Proof.pdf</span>
                        </div>
                      ) : (
                        <img src={prefixUrl(idProofUrl)} alt="ID Proof" className="w-full h-full object-cover absolute inset-0 z-0" />
                      )}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center z-10 p-4">
                        <FileText className="w-8 h-8 text-white mb-2" />
                        <p className="text-xs text-white font-semibold">Change ID document</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <FileText className="w-8 h-8 text-muted-foreground mb-2" />
                      <p className="text-xs text-muted-foreground font-medium mb-1">Aadhaar, Passport or License</p>
                      <p className="text-[10px] text-muted-foreground/60 px-4">PDF, JPG or PNG format</p>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,application/pdf"
                    id="upload-idproof"
                    onChange={(e) => handleMediaUpload(e.target.files?.[0], "id-proof")}
                    className="absolute inset-0 opacity-0 cursor-pointer z-20"
                    disabled={uploadingIdProof}
                  />
                  {uploadingIdProof && (
                    <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-30">
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="w-6 h-6 animate-bounce text-primary" />
                        <span className="text-xs font-semibold">Uploading...</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
            {/* Owners/agents explicitly submit after both uploads; upload alone
                must not create a verified badge or silently enter review. */}
            {["owner", "agent","office", "office_user"].includes(String(user.role || "").toLowerCase()) && (
              <CardFooter className="flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-border/40 pt-5">
                <div className="text-xs">
                  <p className="font-bold text-foreground">
                    {user.selfie_status === "pending_admin_review" && user.id_proof_status === "pending_admin_review"
                      ? "Verification is awaiting admin review"
                      : "Submit both protected documents for review"}
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    {user.phone_verified || user.email_verified
                      ? "Your verified contact method is ready for this request."
                      : "Verify your email or phone before submitting."}
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={handleSubmitVerification}
                  disabled={
                    submittingVerification || !selfieUrl || !idProofUrl ||
                    (!user.phone_verified && !user.email_verified) ||
                    (user.selfie_status === "pending_admin_review" && user.id_proof_status === "pending_admin_review")
                  }
                  className="w-full sm:w-auto font-bold"
                >
                  {submittingVerification ? "Submitting..." : "Submit for Verification"}
                </Button>
              </CardFooter>
            )}
          </Card>

          {/* Professional Profile Details Card */}
          {["agent", "owner", "office", "admin"].includes(String(user.role).toLowerCase()) && (
            <Card className="border border-border/80 shadow-md">
              <CardHeader>
                <CardTitle className="text-xl font-bold">Professional Profile</CardTitle>
                <CardDescription>Manage your real-estate experience, specialisations, localities, and biography.</CardDescription>
              </CardHeader>
              <form onSubmit={handleUpdateProfessionalInfo}>
                <CardContent className="space-y-4">
                  <div className="grid gap-1.5">
                    <Label htmlFor="prof-bio">Biography / Description</Label>
                    <Textarea 
                      id="prof-bio" 
                      value={bio} 
                      onChange={(e) => setBio(e.target.value)} 
                      placeholder="Write a brief professional summary about yourself..." 
                      className="bg-background min-h-[100px]" 
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="grid gap-1.5">
                      <Label htmlFor="prof-exp">Years of Experience</Label>
                      <Input 
                        id="prof-exp" 
                        type="number" 
                        value={experienceYears} 
                        onChange={(e) => setExperienceYears(e.target.value)} 
                        placeholder="e.g. 5" 
                        className="bg-background" 
                        min="0"
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="prof-specialisations">Specialisations</Label>
                      <Input 
                        id="prof-specialisations" 
                        value={specialisations} 
                        onChange={(e) => setSpecialisations(e.target.value)} 
                        placeholder="e.g. Lands, Residential, Commercial" 
                        className="bg-background" 
                      />
                    </div>
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="prof-localities">Coverage Localities</Label>
                    <Input 
                      id="prof-localities" 
                      value={localities} 
                      onChange={(e) => setLocalities(e.target.value)} 
                      placeholder="e.g. Noida Sector 62, Indirapuram, Dwarka" 
                      className="bg-background" 
                    />
                  </div>
                </CardContent>
                <CardFooter className="justify-end border-t border-border/40 pt-4">
                  <Button type="submit" disabled={busy} className="font-bold">{busy ? "Saving Profile..." : "Save Professional Profile"}</Button>
                </CardFooter>
              </form>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
